import styled, { useTheme } from 'styled-components';
import { NAVBAR_HEIGHT } from '../Navbar';
import React from 'react';
import { ActivityContext, ActivityContextType } from '../../contexts/activity';
import Button from '../elements/Button';
import autoAnimate from '@formkit/auto-animate';
import {
  AIChat,
  AIWorkflow,
  MESSAGE_CONTEXT_TYPES,
} from '../../contexts/ai-workflow';
import { ACTIVITY_STEP } from './Content';
import useSelection from '../../hooks/use-selection';
import { toast } from 'react-toastify';
import { QUESTION_TYPE } from './Question';
import { requestMentorReview } from '../../api/teams';
import { useMutation, UseMutationResult } from 'react-query';
import useAuth from '../../hooks/use-auth';
import Modal from '../Modal/Modal';
import { H3, P, PExtraSmall } from '../elements/Text';
import { MultiLineInput } from '../elements/Input';

export const BottomBar = styled.div`
  background-color: ${(props) => props.theme.colors.navigation};
  height: ${NAVBAR_HEIGHT}px;
  min-width: 100vw;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 20px;
  overflow-x: auto;
  gap: 32px;
  border-top: 1px solid ${(props) => props.theme.colors.bgActive};
`;

export const BottomBarButton = styled(Button)`
  margin-top: 0;
  border-radius: 8px;
  padding: 6px 16px;

  p {
    font-size: 12px;
  }
  i {
    font-size: 14px;
  }
`;

export const BottomBarGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const getShouldShowHintFromActivity = (activity: ActivityContextType) => {
  if (activity?.currentStep?.type !== ACTIVITY_STEP.QUESTION) {
    return false;
  }

  const question =
    activity.exercise.questions[activity.currentStep.questionIndex];
  const solution =
    activity.submission.questionSlugToAnswer[
      activity.currentStep?.questionSlug
    ];
  if (
    solution.questionType !== QUESTION_TYPE.PYTHON_PLAYGROUND &&
    solution.questionType !== QUESTION_TYPE.SQL_PLAYGROUND
  ) {
    return true;
  }

  const didNotAttemptPython =
    solution.value === question.initial_py ||
    (!solution.value && !question.initial_py);

  const answerIsEmpty = !solution.value;

  return (
    solution.isRan ||
    solution.isRan === null ||
    didNotAttemptPython ||
    answerIsEmpty
  );
};

const getNumPreviousHints = (activity: ActivityContextType, chat: AIChat) => {
  if (
    !activity.currentStep ||
    activity.currentStep.type !== ACTIVITY_STEP.QUESTION
  ) {
    return 0;
  }

  const currentQuestionIndex = activity.currentStep.questionIndex;
  const solution =
    activity.submission.questionSlugToAnswer[
      activity.currentStep?.questionSlug
    ];
  const previousHints = chat.messages.filter((message) => {
    const messageContext = message.message_context;
    const isHint =
      messageContext && messageContext.context_type === 'question_hint';

    const isUserQuery = message.is_bot === false;
    if (!isHint || !isUserQuery) {
      return false;
    }

    const isSameQuestionAttempt =
      messageContext.context_content.question_index === currentQuestionIndex &&
      messageContext.context_content.attempted_answer == solution.value;
    return isSameQuestionAttempt;
  });

  return previousHints.length;
};

const getHintCopy = (activity: ActivityContextType, chat: AIChat) => {
  if (
    !activity.currentStep ||
    activity.currentStep.type !== ACTIVITY_STEP.QUESTION
  ) {
    return 'Get a hint';
  }

  const numPreviousHints = getNumPreviousHints(activity, chat);

  if (numPreviousHints === 0) {
    return 'Get a hint';
  }

  return 'Get another hint';
};

const AIActions = ({
  type,
  allowAIChatInPreview = false,
  submitActivity,
}: {
  type: string;
  allowAIChatInPreview?: boolean;
  submitActivity: () => Promise<void>;
}) => {
  const [activity] = React.useContext(ActivityContext);
  const {
    setIsChatOpen,
    setMessageContext,
    setPendingAiMessage,
    hasAIAccess,
    chat,
  } = React.useContext(AIWorkflow);
  const { selection, section: selectionSection } = useSelection();

  const parent = React.useRef(null);
  const theme = useTheme();

  React.useEffect(() => {
    parent.current &&
      autoAnimate(parent.current, {
        duration: 300,
        easing: 'ease-in-out',
      });
  }, [parent]);

  const onSummarize = () =>
    setMessageContext({
      context_type: MESSAGE_CONTEXT_TYPES.SUMMARIZE_ACTIVITY,
    });

  const onMotivate = () =>
    setMessageContext({
      context_type: MESSAGE_CONTEXT_TYPES.MOTIVATE_ACTVITY,
    });

  const onAskSelection = () => {
    if (!selection) return;

    setMessageContext({
      context_type: MESSAGE_CONTEXT_TYPES.HIGHLIGHT,
      context_content: {
        section_of_interest: selectionSection ?? '',
        highlighted_text: selection.toString().trim(),
      },
    });
  };

  const onHint = () => {
    if (activity?.currentStep?.type !== ACTIVITY_STEP.QUESTION) {
      return;
    }
    const activeQuestionIndex =
      activity.currentStep?.type === ACTIVITY_STEP.QUESTION
        ? activity.currentStep?.questionIndex
        : null;

    if (activeQuestionIndex === null) {
      return;
    }

    const answer =
      activity.submission.questionSlugToAnswer[
        activity.currentStep?.questionSlug
      ];

    try {
      if (answer.questionType === QUESTION_TYPE.LLM_PLAYGROUND) {
        setMessageContext({
          context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
          context_content: {
            question_index: activeQuestionIndex,
            attempted_answer: answer.value,
            is_correct: answer.isCorrect,
            result: answer.result,
          },
        });
      } else if (
        answer.questionType === QUESTION_TYPE.SQL_MULTIPLE_CHOICE ||
        answer.questionType === QUESTION_TYPE.PYTHON_MULTIPLE_CHOICE ||
        answer.questionType === QUESTION_TYPE.FREE_TEXT_SQL_PLAYGROUND
      ) {
        setMessageContext({
          context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
          context_content: {
            question_index: activeQuestionIndex,
            attempted_answer: answer.value ?? '',
            working_code: answer.working_code,
            is_correct: answer.isCorrect,
          },
        });
      } else if (answer.questionType === QUESTION_TYPE.PYTHON_PLAYGROUND) {
        setMessageContext({
          context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
          context_content: {
            question_index: activeQuestionIndex,
            attempted_answer: answer.value ?? '',
            is_correct: answer.isCorrect,
            failed_correctness_checks: answer.failedCorrectnessChecks ?? [],
          },
        });
      } else if (answer.questionType === QUESTION_TYPE.SQL_PLAYGROUND) {
        setMessageContext({
          context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
          context_content: {
            question_index: activeQuestionIndex,
            attempted_answer: answer.value ?? '',
            is_correct: answer.isCorrect,
          },
        });
      } else {
        setMessageContext({
          context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
          context_content: {
            question_index: activeQuestionIndex,
            attempted_answer: answer.value ?? '',
          },
        });
      }
    } catch (e) {
      toast.error('Sorry, your code did not finish running. Please try again.');
    }
  };

  const onAskQuestion = () => {
    setIsChatOpen(true);
    setPendingAiMessage('What can I help you with?');
  };

  const shouldAllowHint = getShouldShowHintFromActivity(activity);
  const hintCopy = getHintCopy(activity, chat);
  const numPreviousHints = getNumPreviousHints(activity, chat);
  const mentorReviewRequest = getActivityMentorReviewRequest(activity);
  const didRequestReview = mentorReviewRequest?.isRequested ?? false;
  const isExerciseReviewed = !!mentorReviewRequest?.review;

  const shouldAllowMentorRequestreview =
    type === 'TEAMS' && activity?.currentStep?.type === ACTIVITY_STEP.QUESTION;

  // Previews used to have nothing for the AI to act on, so the actions were off
  // wholesale. CMS preview now supplies the draft's content over the socket, so
  // the gate is an explicit prop rather than an inference from the page type.
  // Off by default, so every existing preview behaves exactly as before and only
  // the pages that supply a preview context opt in.
  const isAIEnabled =
    hasAIAccess && (type !== 'PREVIEW' || allowAIChatInPreview);

  const { getTokenSilently } = useAuth();
  const requestMentorReviewMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      if (activity.currentStep?.type === ACTIVITY_STEP.QUESTION) {
        const questionIndex = activity.currentStep.questionIndex;
        const sessionId = activity?.activity_session_id;
        const questionId = activity.exercise.questions[questionIndex].id;

        await requestMentorReview(token, {
          questionId,
          isRequestingReview: !mentorReviewRequest?.isRequested,
          sessionId,
          type: activity.type,
          comment: reviewComment,
        });
        await submitActivity();
      }
    },
  });

  const [isReviewModalOpen, setIsReviewModalOpen] = React.useState(false);
  const [reviewComment, setReviewComment] = React.useState(
    mentorReviewRequest?.comment ?? ''
  );
  const [hasInformedToUseHint, setHasInformedToUseHint] = React.useState(false);

  const handleRequestReviewClick = () => {
    if (numPreviousHints === 0 && !hasInformedToUseHint) {
      toast.info('Try the "Get a hint" feature first!');
      setHasInformedToUseHint(true);
      return;
    }
    setIsReviewModalOpen(true);
  };

  React.useEffect(() => {
    if (activity.currentStep?.type === ACTIVITY_STEP.QUESTION) {
      setReviewComment(mentorReviewRequest?.comment ?? '');
      // setHasInformedToUseHint(false);
    }
  }, [activity.currentStep?.type, mentorReviewRequest?.comment]);

  return (
    <BottomBarGroupWrapper ref={parent}>
      {shouldAllowMentorRequestreview && (
        <div id="mentor-request-review">
          <BottomBarButton
            type={'secondary'}
            color={theme.colors.buttonPrimary}
            label={
              isExerciseReviewed
                ? 'Mentor replied'
                : didRequestReview
                ? 'SOS requested'
                : 'SOS'
            }
            isDisabled={isExerciseReviewed}
            faIcon={'fa-duotone fa-life-ring'}
            showIconBeforeLabel
            onClick={handleRequestReviewClick}
            isLoading={requestMentorReviewMutation.isLoading}
          />
        </div>
      )}
      {isAIEnabled && (
        <BottomBarButton
          id="ai-ask-question"
          type="secondary"
          color={theme.colors.buttonSecondary}
          label={'Ask a question'}
          faIcon="fa-duotone fa-sparkles"
          showIconBeforeLabel
          onClick={onAskQuestion}
        />
      )}
      {activity?.currentStep?.type === ACTIVITY_STEP.LESSON && isAIEnabled && (
        <>
          <BottomBarButton
            id="ai-summarize-activity"
            type="secondary"
            color={theme.colors.buttonSecondary}
            label={'Summarize Activity'}
            faIcon="fa-duotone fa-sparkles"
            showIconBeforeLabel
            onClick={onSummarize}
          />
          <BottomBarButton
            id="ai-motivate-me"
            type="secondary"
            color={theme.colors.buttonSecondary}
            label={'Motivate Me'}
            faIcon="fa-duotone fa-sparkles"
            showIconBeforeLabel
            onClick={onMotivate}
          />
        </>
      )}
      {activity?.currentStep?.type === ACTIVITY_STEP.QUESTION &&
        isAIEnabled && (
          <BottomBarButton
            id="ai-hint"
            type="secondary"
            color={theme.colors.buttonSecondary}
            label={hintCopy}
            isDisabled={!shouldAllowHint}
            faIcon="fa-duotone fa-sparkles"
            showIconBeforeLabel
            onClick={onHint}
            tooltip={shouldAllowHint ? '' : 'Run your code first!'}
          />
        )}
      {selection && isAIEnabled && (
        <BottomBarButton
          id="ai-what-does-this-mean"
          type="primary"
          color={theme.colors.buttonSecondary}
          label={'What does this mean?'}
          faIcon="fa-duotone fa-sparkles"
          showIconBeforeLabel
          onClick={onAskSelection}
        />
      )}

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        reviewComment={reviewComment}
        setReviewComment={setReviewComment}
        didRequestReview={didRequestReview}
        requestMentorReviewMutation={requestMentorReviewMutation}
      />
    </BottomBarGroupWrapper>
  );
};

export default function ActionBar({
  allowAIChatInPreview = false,
  isSubmitting,
  submitAndMoveToReview,
  submitAndMoveToQuestion,
  submitAndMoveToNextLesson,
  submitAndMoveToLesson,
  submitActivity,
  type,
}: {
  allowAIChatInPreview?: boolean;
  submitAndMoveToReview: () => void;
  submitAndMoveToQuestion: ({
    questionIndex,
    questionSlug,
  }: {
    questionIndex: number;
    questionSlug: string;
  }) => void;
  submitAndMoveToNextLesson: () => void;
  submitAndMoveToLesson: () => void;
  isSubmitting: boolean;
  type: string;
  submitActivity: () => Promise<void>;
}) {
  return (
    <BottomBar>
      <AIActions
        type={type}
        allowAIChatInPreview={allowAIChatInPreview}
        submitActivity={submitActivity}
      />
      <NavButtons
        submitAndMoveToReview={submitAndMoveToReview}
        submitAndMoveToQuestion={submitAndMoveToQuestion}
        submitAndMoveToNextLesson={submitAndMoveToNextLesson}
        submitAndMoveToLesson={submitAndMoveToLesson}
        isSubmitting={isSubmitting}
      />
    </BottomBar>
  );
}

const NavButtons = ({
  submitAndMoveToReview,
  submitAndMoveToQuestion,
  submitAndMoveToLesson,
  submitAndMoveToNextLesson,
  isSubmitting,
}: {
  submitAndMoveToReview: () => void;
  submitAndMoveToQuestion: ({
    questionIndex,
    questionSlug,
  }: {
    questionIndex: number;
    questionSlug: string;
  }) => void;
  submitAndMoveToLesson: () => void;
  submitAndMoveToNextLesson: () => void;
  isSubmitting: boolean;
}) => {
  const [activity, dispatch] = React.useContext(ActivityContext);
  const currentStep = activity?.currentStep;
  const theme = useTheme();

  const numQuestions = activity.exercise.questions.length;
  const canGoBack = currentStep?.type !== ACTIVITY_STEP.LESSON;
  const hasAnsweredAllQuestions = activity.exercise.questions.every(
    (question) =>
      activity.submission.questionSlugToAnswer[question.slug].isAnswered
  );
  const hasAIReview = activity.latestSavedSubmission?.ai_review;

  const onBack = () => {
    if (currentStep?.type === ACTIVITY_STEP.FEEDBACK) {
      return submitAndMoveToQuestion({
        questionIndex: numQuestions - 1,
        questionSlug: activity.exercise.questions[numQuestions - 1].slug,
      });
    }

    if (currentStep?.type === ACTIVITY_STEP.QUESTION) {
      if (currentStep.questionIndex === 0) {
        return submitAndMoveToLesson();
      }
      return submitAndMoveToQuestion({
        questionIndex: currentStep.questionIndex - 1,
        questionSlug:
          activity.exercise.questions[currentStep.questionIndex - 1].slug,
      });
    }
  };

  const currentQuestionAnswer =
    currentStep?.type === ACTIVITY_STEP.QUESTION
      ? activity.submission.questionSlugToAnswer[
          (
            currentStep as {
              type: ACTIVITY_STEP.QUESTION;
              questionSlug: string;
            }
          ).questionSlug
        ]
      : null;

  const shouldConfirmNext =
    currentStep?.type === ACTIVITY_STEP.QUESTION &&
    !currentQuestionAnswer?.isAnswered;

  const onNext = async () => {
    if (currentStep?.type === ACTIVITY_STEP.LESSON) {
      return dispatch({
        type: 'SET_PAGE_STEP',
        payload: {
          step: {
            type: ACTIVITY_STEP.QUESTION,
            questionIndex: 0,
            questionSlug: activity.exercise.questions[0].slug,
          },
        },
      });
    }

    if (
      currentStep?.type === ACTIVITY_STEP.QUESTION &&
      currentStep.questionIndex === numQuestions - 1
    ) {
      if (
        hasAIReview ||
        hasAnsweredAllQuestions ||
        window.confirm(
          'We recommend attempting all exercises before moving to the review. Do you want to continue?'
        )
      ) {
        submitAndMoveToReview();
      }
      return;
    }

    if (currentStep?.type === ACTIVITY_STEP.QUESTION) {
      if (
        currentQuestionAnswer?.isAnswered ||
        window.confirm(
          "You haven't tried this exercise yet. Are you sure you want to move on?"
        )
      ) {
        submitAndMoveToQuestion({
          questionIndex: currentStep.questionIndex + 1,
          questionSlug:
            activity.exercise.questions[currentStep.questionIndex + 1].slug,
        });
      }
      return;
    }

    if (currentStep?.type === ACTIVITY_STEP.FEEDBACK) {
      return submitAndMoveToNextLesson();
    }
  };

  const shouldLockNext = false;

  return (
    <BottomBarGroupWrapper>
      <BottomBarButton
        color={theme.colors.textPlaceholder}
        label="Back"
        type="secondary"
        tooltip={canGoBack ? '' : 'You are at the beginning'}
        faIcon="fa-solid fa-arrow-left"
        showIconBeforeLabel
        isDisabled={!canGoBack}
        onClick={onBack}
      />

      <BottomBarButton
        style={{ minWidth: '150px' }}
        isDisabled={isSubmitting || shouldLockNext}
        tooltip={
          shouldConfirmNext || shouldLockNext ? 'Try the exercise first!' : ''
        }
        label="Next"
        type={'primary'}
        faIcon="fa-solid fa-arrow-right"
        onClick={onNext}
        isLoading={isSubmitting}
      />
    </BottomBarGroupWrapper>
  );
};

function getActivityMentorReviewRequest(activity: ActivityContextType) {
  if (activity.currentStep?.type !== ACTIVITY_STEP.QUESTION) {
    return null;
  }

  const questionIndex = activity.currentStep.questionIndex;
  const questionId = activity.exercise.questions[questionIndex].id;

  const mentorReview =
    activity.latestSavedSubmission?.mentor_reviews?.find(
      (review) => review.questionId === questionId
    ) ?? null;

  return mentorReview;
}

const ModalWrapper = styled.div`
  padding: 24px;
  margin-bottom: 24px;
  width: 800px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const CloseButton = styled.i.attrs({
  className: 'fa fa-times',
})`
  cursor: pointer;
  font-size: 24px;
  color: ${(props) => props.theme.colors.textPrimary};
`;

const ReviewModalHeader = ({ onClose }: { onClose: () => void }) => (
  <HeaderWrapper>
    <H3 style={{ margin: 0 }}>Request Help from Your Mentor</H3>
    <CloseButton onClick={onClose} />
  </HeaderWrapper>
);

const ReviewModal = ({
  isOpen,
  onClose,
  reviewComment,
  setReviewComment,
  didRequestReview,
  requestMentorReviewMutation,
}: {
  isOpen: boolean;
  onClose: () => void;
  reviewComment: string;
  setReviewComment: React.Dispatch<React.SetStateAction<string>>;
  didRequestReview: boolean;
  requestMentorReviewMutation: UseMutationResult<void, unknown, void, unknown>;
}) => {
  const theme = useTheme();
  if (!isOpen) return null;

  return (
    <Modal
      style={{ backgroundColor: theme.colors.bgPage2, width: 'fit-content' }}
    >
      <ModalWrapper>
        <ReviewModalHeader onClose={onClose} />
        <P>Your mentor will see your current exercise screen as it is now.</P>
        <P>
          Add any other context to help your mentor understand your question or
          challenge. The more details, the better!
        </P>
        <MultiLineInput
          placeholder="Enter your comment here..."
          value={reviewComment}
          onChange={(e) => setReviewComment(e.target.value)}
          maxRows={10}
        />
        <PExtraSmall
          style={{
            marginTop: 0,
            fontWeight: 400,
            color: theme.colors.inputTextPlaceholder,
          }}
        >
          PS: Press Enter to add a new line
        </PExtraSmall>
        <Button
          type={didRequestReview ? 'secondary' : 'primary'}
          label={didRequestReview ? 'Withdraw your request' : 'Request help'}
          faIcon={
            didRequestReview
              ? 'fa-regular fa-undo-alt'
              : 'fa-duotone fa-check-double'
          }
          showIconBeforeLabel
          onClick={async () => {
            await requestMentorReviewMutation.mutateAsync();
            onClose();
            if (didRequestReview) {
              toast.info('Cancelled your mentor review request.');
            } else {
              toast.success(
                'Request sent to your mentor! They will get back to you soon.'
              );
            }
          }}
          isLoading={requestMentorReviewMutation.isLoading}
        />
      </ModalWrapper>
    </Modal>
  );
};
